import { useApi } from './useApi';
import SparkMD5 from 'spark-md5';

// eslint-disable-next-line max-lines-per-function
export function useCos() {
  const api = useApi();

  // 对更多字符编码的 url encode 格式
  const camSafeUrlEncode = function(str: string) {
    return encodeURIComponent(str)
      .replace(/!/ug, '%21')
      .replace(/'/ug, '%27')
      .replace(/\(/ug, '%28')
      .replace(/\)/ug, '%29')
      .replace(/\*/ug, '%2A');
  };

  // 获取签名
  const getAuthorization = async(options: any) => {
    const res = await api.cosApi.getPostPolicycos({
      ext: options.ext,
      md5: options.md5,
    });
    if (!res.data?.data) {
      throw new Error('获取签名失败');
    }
    return res.data.data;
  };

  /**
   * prefix: 请求 cos 的 url
   * file: Web端的File对象
   * key: 上传到 cos 的路径
   * formData: 服务端返回的鉴权参数
   * onProgress: 上传进度回调函数
   */
  const postFile = function({ prefix, file, key, formData, onProgress }: any) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // 创建FormData
      const uploadFormData = new FormData();

      // 添加腾讯云COS所需的参数
      Object.keys(formData).forEach((k) => {
        uploadFormData.append(k, formData[k]);
      });

      // 添加文件，必须在最后添加
      uploadFormData.append('file', file);

      // 监听上传进度
      if (onProgress && typeof onProgress === 'function') {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      // 监听响应
      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          const url = `${prefix}/${camSafeUrlEncode(key).replace(/%2F/ug, '/')}`;
          const path = `${camSafeUrlEncode(key).replace(/%2F/ug, '/')}`;
          resolve({
            url,
            path,
          });
        } else {
          reject(new Error(`上传失败，状态码: ${xhr.status}`));
        }
      });

      // 监听错误
      xhr.addEventListener('error', () => {
        reject(new Error('网络错误'));
      });

      // 发送请求
      xhr.open('POST', prefix);
      xhr.send(uploadFormData);
    });
  };

  // 计算文件MD5值
  const getFileMd5 = (file: File | Blob): Promise<string> => new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    const spark = new SparkMD5.ArrayBuffer();

    fileReader.onload = (event) => {
      if (event.target?.result) {
        spark.append(event.target.result as ArrayBuffer);
        resolve(spark.end());
      } else {
        reject(new Error('文件读取失败'));
      }
    };

    fileReader.onerror = () => {
      reject(new Error('文件读取错误'));
    };

    fileReader.readAsArrayBuffer(file);
  });

  const getFormData = async(file: File | Blob, fileName?: string) => {
    const md5 = await getFileMd5(file);
    console.log('文件MD5:', md5);
    console.log('文件大小:', file.size);

    // 获取文件扩展名
    let fileExt = '';
    if (fileName) {
      const extIndex = fileName.lastIndexOf('.');
      fileExt = extIndex >= 0 ? fileName.substring(extIndex + 1) : '';
    } else if (file instanceof File) {
      const extIndex = file.name.lastIndexOf('.');
      fileExt = extIndex >= 0 ? file.name.substring(extIndex + 1) : '';
    }

    const AuthData = await getAuthorization({ ext: fileExt, md5 });
    // 传入文件后缀名，服务端生成带签名的 url
    console.log('认证数据:', AuthData);

    // 请求用到的参数
    // 请求 url
    const prefix = `https://${AuthData.cosHost}`;
    // 让服务端来决定文件名更安全
    const key = AuthData.cosKey;
    const formData: any = {
      key,
      'success_action_status': 200,
      'Content-Type': '',
      'q-sign-algorithm': AuthData.qSignAlgorithm,
      'q-ak': AuthData.qAk,
      'q-key-time': AuthData.qKeyTime,
      'q-signature': AuthData.qSignature,
      'policy': AuthData.policy,
    };

    if (AuthData.securityToken) {
      formData['x-cos-security-token'] = AuthData.securityToken;
    }

    return {
      formData,
      prefix,
      key,
      md5,
      fileExt,
      size: file.size,
      name: fileName || (file as any).name,
    };
  };

  // Web端文件上传函数
  const uploadFile = async(file: File | Blob, fileName?: string, onProgress?: (percent: number) => void) => {
    const { formData, prefix, key, md5, fileExt, size, name } = await getFormData(file, fileName);
    const res: any = await postFile({ prefix, file, key, formData, onProgress });
    return {
      ...res,
      md5,
      fileExt,
      size,
      name,
    } as {url: string, path: string, md5: string, fileExt: string, size: number, name: string};
  };

  const getFile = async(url: string, page: number) => {
    try {
      const response = await fetch(`${
        url.replace('print-1356239298.cos.ap-guangzhou.myqcloud.com', 'pcos.lvxinyunyin2025.com')
      }?ci-process=doc-preview&page=${page}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 调试：打印所有可用的响应头
      console.log('所有响应头:');
      for (const [key, value] of response.headers.entries()) {
        console.log(`${key}: ${value}`);
      }

      // 尝试多种方式获取总页数
      let totalPage = 0;

      // 方式1：标准方式
      const totalPageHeader1 = response.headers.get('X-Total-Page');
      console.log('X-Total-Page (标准方式):', totalPageHeader1);

      // 方式2：小写
      const totalPageHeader2 = response.headers.get('x-total-page');
      console.log('x-total-page (小写):', totalPageHeader2);

      // 方式3：其他可能的名称
      const totalPageHeader3 = response.headers.get('total-page');
      console.log('total-page:', totalPageHeader3);

      // 方式4：检查是否有 Access-Control-Expose-Headers
      const exposeHeaders = response.headers.get('Access-Control-Expose-Headers');
      console.log('Access-Control-Expose-Headers:', exposeHeaders);

      // 取第一个有效值
      totalPage = Number(totalPageHeader1 || totalPageHeader2 || totalPageHeader3) || 0;

      console.log('最终获取到的totalPage:', totalPage);

      // 如果无法获取总页数，给出警告和建议
      if (totalPage === 0) {
        console.warn('⚠️ 无法从响应头获取总页数');
        console.warn('可能的解决方案：');
        console.warn('1. 请联系后端开发者在响应中添加: Access-Control-Expose-Headers: X-Total-Page');
        console.warn('2. 或者考虑通过其他API接口获取文档总页数');
        console.warn('3. 当前可用的响应头:', Array.from(response.headers.keys()).join(', '));
      }

      // 获取文件blob
      const blob = await response.blob();

      // 创建临时URL供预览使用
      const imageUrl = URL.createObjectURL(blob);

      return {
        totalPage,
        imageUrl,
      };
    } catch (error) {
      console.error('获取文件失败:', error);
      throw error;
    }
  };


  return {
    getFormData,
    uploadFile,
    getFile,
  };
}

/*
使用示例：

在Vue组件中使用：

<template>
  <div>
    <input type="file" @change="handleFileChange" />
    <div v-if="uploading">上传进度: {{ progress }}%</div>
    <div v-if="uploadedUrl">上传成功: {{ uploadedUrl }}</div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useCos } from '@/hooks/useCos';
import { ElMessage } from 'element-plus';

const { uploadFile } = useCos();
const uploading = ref(false);
const progress = ref(0);
const uploadedUrl = ref('');

const handleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) return;

  try {
    uploading.value = true;
    progress.value = 0;

    const result = await uploadFile(file, file.name, (percent) => {
      progress.value = percent;
    });

    uploadedUrl.value = result.url;
    ElMessage.success('文件上传成功');
  } catch (error) {
    console.error('上传失败:', error);
    ElMessage.error('文件上传失败');
  } finally {
    uploading.value = false;
  }
};
</script>

也可以上传Blob对象：

const blob = new Blob(['Hello World'], { type: 'text/plain' });
const result = await uploadFile(blob, 'hello.txt');
*/
